import type { SqlError, SqlWarning } from "../types";

/**
 * SQL result-stream protocol types and reader, shared by the sql editors and the
 * extension engine. Kept as a leaf module so stream consumers do not pull the
 * editor stack into their bundle.
 */
/**
 * A column of a dataset as the header line of the stream describes it. `type` is the bare SQL type name the
 * consumers key on; the server adds the declared type and its parameters when it knows them (an older server
 * sends the name and the bare type only).
 */
export type SqlColumn = {
	name: string,
	/** bare SQL type name: nvarchar, decimal, datetime2... (money comes as decimal, rowversion as binary) */
	type?: string,
	/** the type as declared: nvarchar(50), nvarchar(max), decimal(18,4), datetime2(7) */
	typeName?: string,
	/** declared length of the character and binary types, 0 = max */
	size?: number,
	/** precision of the numeric and date-time types */
	precision?: number,
	/** scale of the numeric types, fractional seconds precision of the date-time types */
	scale?: number,
	/** nullability when the driver knows it (an expression is reported as nullable) */
	nullable?: boolean,
}

export type SqlDataset = {
	index: number,
	columns: SqlColumn[],
	data: string[][],
	description?: string,
}

export type SqlMessage = {
	index?: number,
	errors?: SqlError[],
	messages?: SqlWarning[],
	description?: string,
	content?: string
};

/** Which messages of an executed batch the user wants to see (setting `editor.sql.messages`); errors always get through */
export type SqlMessagesLevel = 'errors' | 'warnings' | 'all';

/**
 * The message as the level lets it through: errors and the rest of the protocol always, the warnings
 * (code > 0) from the 'warnings' level up, the PRINT output (code 0) only with 'all'. Null when nothing
 * of a warnings / print message remains.
 */
export function filterSqlMessage(message: SqlMessage, level: SqlMessagesLevel): SqlMessage | null {
	if (!message?.messages || level === 'all') return message ?? null;
	if (level === 'errors') return null;
	const warnings = message.messages.filter(m => m.code > 0);
	return warnings.length ? {...message, messages: warnings} : null;
}

export type SqlQueryPlan = {
	index?: number,
	xml?: string
};

export type SqlExecCallbackArgs = {
	dataset?: SqlDataset,
	message?: SqlMessage,
	queryPlan?: SqlQueryPlan,
	spid?: number,
}

export async function readResultStream(reader: ReadableStreamDefaultReader<string[]>, cb?: (args: SqlExecCallbackArgs) => void) {
	let done = false, datasetEnd = false, columns: SqlColumn[] = null, data: string[][] = null, queryPlan = false;
	let datasetIndex = 0, queryPlanIndex = 0, json: Record<string, unknown> = null, description: string = null;
	while (!done) {
		const result = await reader.read();
		done = result.done;
		if ((!result.value) || (result.value.length == 1 && result.value[0] === "\u001E")) {
			datasetEnd = true;
		} else {
			if (queryPlan) {
				if (cb) cb({queryPlan: {index: queryPlanIndex++, xml: result.value[0]}});
				queryPlan = false;
				columns = null;
				datasetEnd = false;
				continue;
			} else if (!columns) {
				datasetEnd = false;
				json = null;
				try { json = JSON.parse(result.value[0]) } catch (error) {
					json = null;
				}

				if (json) {
					if (json.spid) {
						if (cb) cb({spid: json.spid as number});
					}
					if (json.query_plan) {
						queryPlan = true;
						continue;
					} else if (json.columns) { // columns
						columns = json.columns as SqlColumn[];
						description = json.description as string;
					} else if (json.messages) { // warnings / print output (before the errors branch - a deployment line carries a description too)
						if (cb) cb({message: {index: datasetIndex++, messages: json.messages as SqlWarning[], description: json.description as string}});
						continue;
					} else if (json.errors || json.description) { // message
						if (cb) cb({message: {index: datasetIndex++, errors: json.errors as SqlError[], description: json.description as string}});
						continue;
					} else { // protocol-only line carrying no dataset, message or plan
						continue;
					}
				}
				data = [];
			} else {
				data.push(result.value);
			}
		}

		if (datasetEnd && data) {
			if (cb) cb({dataset: {index: datasetIndex++, columns: columns, data: data, description: description}});
			datasetEnd = false;
			columns = null;
			data = null;
			description = null;
		}
	}
}
